import React, { useState, useEffect } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Building2, ArrowRight, Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { apiService, AuditClient } from "@/services/api-service";
import { TitleBar } from "@/components/TitleBar";

export default function ClientSelectionPage() {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState<AuditClient[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isAuthenticated) {
      navigate({ to: "/login" });
      return;
    }

    loadClients();
  }, [isAuthenticated, navigate]);

  const loadClients = async () => {
    try {
      setIsLoading(true);
      setError("");
      
      // Try to get clients where user is partner/manager first
      let userClients: AuditClient[] = [];
      
      try {
        userClients = await apiService.getUserClients();
      } catch (err: any) {
        // If that fails, try getting all engagements and extracting clients
        console.log("getUserClients failed, trying engagements approach:", err);
        
        try {
          // Get all engagements for the user to find unique clients
          const engagements = await apiService.getUserEngagements();
          
          // Extract unique client IDs from engagements
          const clientIds = [...new Set(engagements.map(e => e.audit_client_id).filter(Boolean))];
          
          if (clientIds.length > 0) {
            // Try to get all clients and filter
            try {
              const allClients = await apiService.getAllClients();
              userClients = allClients.filter(client => 
                clientIds.includes(client.id)
              );
            } catch (allClientsErr: any) {
              // If getAllClients fails, we can't get client details
              console.error("getAllClients failed:", allClientsErr);
              // We'll show an error but still allow engagement selection
            }
          }
        } catch (engagementsErr: any) {
          console.error("getUserEngagements failed:", engagementsErr);
          throw engagementsErr;
        }
      }

      setClients(userClients);
      
      // If no clients found but no error, it might be that user has no engagements yet
      if (userClients.length === 0 && !error) {
        setError(""); // Clear error, show empty state message
      }
    } catch (err: any) {
      console.error("Error loading clients:", err);
      // Only set error if it's a real error, not just "no clients"
      if (err.message && !err.message.includes("not found") && !err.message.includes("No")) {
        setError(err.message || "Failed to load clients");
      } else {
        setError(""); // Clear error for "not found" cases
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleClientSelect = (clientId: string) => {
    navigate({ to: "/engagements", search: { clientId } });
  };

  if (isLoading) {
    return (
      <>
        <TitleBar />
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-black to-gray-900 p-4 pt-10">
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-muted-foreground">Loading clients...</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <TitleBar />
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 p-4 pt-10">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-white mb-2">Select Client</h1>
            <p className="text-muted-foreground">
              Choose a client to view available engagements
            </p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-500/20 border border-red-500/50 rounded-md">
              <p className="text-red-400">{error}</p>
            </div>
          )}

          {clients.length === 0 ? (
            <Card className="bg-black/95 border-white/10">
              <CardContent className="p-8 text-center">
                <Building2 className="h-12 w-12 mx-auto mb-4 text-white/40" />
                <p className="text-muted-foreground">No clients found. Please contact your administrator.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {clients.map((client) => (
                <Card
                  key={client.id}
                  className="bg-black/95 border-white/10 hover:border-blue-500/50 transition-colors cursor-pointer"
                  onClick={() => handleClientSelect(client.id)}
                >
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <Building2 className="h-6 w-6 text-blue-500" />
                      <ArrowRight className="h-5 w-5 text-white/40" />
                    </div>
                    <CardTitle className="text-white mt-4">{client.client_name}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between">
                      <span className={`text-sm px-2 py-1 rounded ${
                        client.status === 'Active' 
                          ? 'bg-green-500/20 text-green-400' 
                          : 'bg-gray-500/20 text-gray-400'
                      }`}>
                        {client.status}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

